// Getting data from the server. The route loader, the arrivals poller and
// the store they update. No DOM. Imported by the page, tests and benchmarks,
// so they all use the same update contract.

import * as Core from './core.ts';
import { prepareMergeTrainsSteps } from './live-motion.ts';
import type {
  ComputedTrains,
  Arrival,
  LineId,
  Network,
  RouteDirection,
  RouteResponse,
  RouteSequence,
  TrainStore,
} from './types.ts';

// The parts of a fetch Response that getJSON reads.
type ResponseHeaders = Readonly<{ get: (name: string) => string | null | undefined }>;
export type FetchResponse = Readonly<{
  ok: boolean;
  status: number;
  headers: ResponseHeaders;
  json: () => Promise<unknown>;
}>;
type FetchInit = Readonly<{ headers: Readonly<Record<string, string>>; cache: RequestCache; signal: AbortSignal }>;
export type FetchLike = (url: string, init: FetchInit) => Promise<FetchResponse>;
export type ClientOptions = Readonly<{ base: string; fetch?: FetchLike; clock?: () => number }>;

// A response body and its x-data-time header (TfL's snapshot time, server
// clock, 0 when absent).
export type SampledJSON = Readonly<{ data: unknown; dataTime: number }>;

export interface ApiClient {
  offset: number;
  readonly getJSON: (path: string, signal?: AbortSignal) => Promise<unknown>;
  readonly getSampledJSON: (path: string, signal?: AbortSignal) => Promise<SampledJSON>;
}

// A signal that aborts when either input does, with that input's reason,
// and a function that detaches it from the inputs. Safari before 17.4 has
// no AbortSignal.any.
function eitherSignal(a: AbortSignal, b: AbortSignal): readonly [AbortSignal, () => void] {
  if (typeof AbortSignal.any === 'function') return [AbortSignal.any([a, b]), (): void => {}];
  const combined = new AbortController();
  const detach = (): void => {
    a.removeEventListener('abort', onA);
    b.removeEventListener('abort', onB);
  };
  const onA = (): void => {
    detach();
    combined.abort(a.reason);
  };
  const onB = (): void => {
    detach();
    combined.abort(b.reason);
  };
  if (a.aborted) combined.abort(a.reason);
  else if (b.aborted) combined.abort(b.reason);
  else {
    a.addEventListener('abort', onA);
    b.addEventListener('abort', onB);
  }
  return [combined.signal, detach];
}

// JSON from the Worker, plus the server clock. `offset` is server clock
// minus local clock, in ms.
function createClient({
  base,
  fetch: doFetch = (url, init) => fetch(url, init),
  clock = () => Date.now(),
}: ClientOptions): ApiClient {
  async function getSampledJSON(path: string, signal?: AbortSignal): Promise<SampledJSON> {
    const started = clock();
    const timeout = AbortSignal.timeout(15000);
    const [requestSignal, detach] = signal ? eitherSignal(signal, timeout) : [timeout, (): void => {}];
    let res: FetchResponse;
    let data: unknown;
    try {
      res = await doFetch(base + path, {
        headers: { accept: 'application/json' },
        cache: 'no-store',
        signal: requestSignal,
      });
      if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      data = await res.json();
      requestSignal.throwIfAborted();
    } finally {
      detach();
    }
    const st = Number(res.headers.get('x-server-time'));
    const age = Number(res.headers.get('age')) || 0;
    // A suspended tab can deliver a response long after it was requested.
    // Such a round trip is not a usable clock sample.
    const done = clock();
    if (st !== 0 && !Number.isNaN(st) && done - started < 5000) client.offset = st + age * 1000 - (started + done) / 2;
    return { data, dataTime: Number(res.headers.get('x-data-time')) || 0 };
  }
  const client: ApiClient = {
    offset: 0,
    getJSON: async (path, signal) => (await getSampledJSON(path, signal)).data,
    getSampledJSON,
  };
  return client;
}

// ---- network routes

function validRoute(data: unknown): data is RouteSequence {
  return (
    typeof data === 'object' && data !== null && 'stopPointSequences' in data && Array.isArray(data.stopPointSequences)
  );
}

export type RouteJob = Readonly<{ lineId: LineId; direction: RouteDirection }>;

// How many routes arrived this time, which are still missing and the last
// error (null if none).
export type RouteLoadResult = Readonly<{ added: number; failed: readonly RouteJob[]; error: unknown }>;

export type RouteProgress = (done: number, total: number) => void;

type RouteFetcher = (lineId: LineId, direction: RouteDirection) => Promise<unknown>;
export type RouteLoaderOptions = Readonly<{
  lineIds: readonly LineId[];
  fetchRoute: RouteFetcher;
  concurrency?: number;
}>;
type RouteLoad = (onProgress?: RouteProgress) => Promise<RouteLoadResult>;
export type RouteLoader = Readonly<{
  load: RouteLoad;
  missing: () => RouteJob[];
  responses: () => RouteResponse[];
  size: number;
}>;

// setTimeout and clearTimeout, injectable so tests can drive time.
type TimerHandle = ReturnType<typeof setTimeout>;
export type Timers<H = TimerHandle> = Readonly<{
  setTimeout: (fn: () => unknown, ms: number) => H;
  clearTimeout: (h?: H) => void;
}>;

const realTimers: Timers = {
  setTimeout: (fn, ms) =>
    setTimeout(() => {
      fn();
    }, ms),
  clearTimeout: (id) => {
    clearTimeout(id);
  },
};

// Fetch `jobs`, `concurrency` at a time, passing each valid route to `add`.
type AddRoute = (response: RouteResponse) => void;
async function fetchRoutes(jobs: readonly RouteJob[], o: RouteLoaderOptions, onProgress: RouteProgress, add: AddRoute) {
  const total = jobs.length;
  const failed: RouteJob[] = [];
  let added = 0;
  let done = 0;
  let error: unknown = null;
  let next = 0;
  const worker = async (): Promise<void> => {
    const job = jobs[next++];
    if (!job) return;
    try {
      const data = await o.fetchRoute(job.lineId, job.direction);
      if (!validRoute(data)) throw new Error('Invalid route data');
      add({ ...job, data });
      added++;
    } catch (e) {
      failed.push(job);
      error = e;
    }
    done++;
    onProgress(done, total);
    await worker();
  };
  await Promise.all(Array.from({ length: o.concurrency ?? 6 }, worker));
  const result: RouteLoadResult = { added, failed, error };
  return result;
}

// Route sequences for every line and direction. load() fetches whatever is
// still missing, so a failed route is retried by calling it again.
function createRouteLoader(options: RouteLoaderOptions): RouteLoader {
  const all: RouteJob[] = [];
  const directions: readonly RouteDirection[] = ['outbound', 'inbound'];
  for (const lineId of options.lineIds) for (const direction of directions) all.push({ lineId, direction });
  // 'line|direction' -> { lineId, direction, data }
  const routes = new Map<string, RouteResponse>();
  const keyOf = (job: RouteJob): string => job.lineId + '|' + job.direction;
  let loading: Promise<RouteLoadResult> | null = null;

  function missing(): RouteJob[] {
    return all.filter((job) => !routes.has(keyOf(job)));
  }

  async function load(onProgress: RouteProgress = () => {}): Promise<RouteLoadResult> {
    if (loading) return loading;
    loading = fetchRoutes(missing(), options, onProgress, (response) => {
      routes.set(keyOf(response), response);
    });
    try {
      return await loading;
    } finally {
      loading = null;
    }
  }

  return {
    load,
    missing,
    responses: () => [...routes.values()],
    get size() {
      return routes.size;
    },
  };
}

export interface KeepRoutesOptions {
  readonly loader: RouteLoader;
  readonly onChange: (responses: readonly RouteResponse[]) => Promise<void> | void;
  readonly onProgress?: RouteProgress;
  // The attempt's result and how many routes are loaded.
  readonly onFailure?: (result: RouteLoadResult, loaded: number) => void;
  readonly timers?: Pick<Timers<unknown>, 'setTimeout'> & Partial<Pick<Timers<unknown>, 'clearTimeout'>>;
  readonly retryMs?: number;
  readonly maxRetryMs?: number;
  readonly log?: { readonly error: (...args: readonly unknown[]) => void };
}

// Startup and recovery are one cycle: load what's missing, report any new
// routes (onChange, awaited), and while any are still missing, try again
// with backoff. A total outage at startup just means onChange comes later.
// start() resolves after the first attempt.
function keepRoutes(
  options: KeepRoutesOptions,
): Readonly<{ start: () => Promise<RouteLoadResult>; cancel: () => void }> {
  const { loader, onChange, onFailure = () => {}, maxRetryMs = 300000, log = console } = options;
  const timers = options.timers ?? realTimers;
  const clearTimer: ((handle?: unknown) => void) | undefined = options.timers
    ? options.timers.clearTimeout
    : (handle) => {
        Reflect.apply(realTimers.clearTimeout, realTimers, [handle]);
      };
  let delay = options.retryMs ?? 10000;
  let publishedSize = 0;
  let active = true;
  let timer: unknown;
  async function attempt(progress?: RouteProgress): Promise<RouteLoadResult> {
    const result = await loader.load(progress);
    if (!active) return result;
    try {
      // The first routes may already be in from a startup prime.
      if (loader.size > publishedSize) {
        publishedSize = loader.size;
        await onChange(loader.responses());
      }
    } catch (e) {
      log.error('Network update failed', e);
    }
    if (active && loader.missing().length > 0) {
      onFailure(result, loader.size);
      timer = timers.setTimeout(() => (active ? attempt() : undefined), delay);
      delay = Math.min(delay * 2, maxRetryMs);
    }
    return result;
  }
  return {
    start: () => attempt(options.onProgress),
    cancel() {
      active = false;
      clearTimer?.(timer);
    },
  };
}

// ---- arrivals

// What the poller maintains, and what the page reads.
function createStore(): TrainStore {
  return { trains: new Map(), byStation: new Map(), groupUpdates: new Map(), groupTimes: new Map(), failedGroups: [] };
}

// What the poller needs from a city's data: which items of an arrivals
// response are predictions for a group of lines, and how those become trains.
export interface FeedCore<P> {
  readonly isPrediction: (p: unknown, group: readonly LineId[]) => p is P;
  readonly computeTrains: (net: Network, predictions: readonly P[], now: number) => ComputedTrains;
  readonly mergeTrains: typeof Core.mergeTrains;
}

// An object naming one of the group's lines: the usual isPrediction.
function isGroupPrediction(p: unknown, group: readonly LineId[]): p is { readonly lineId: LineId } {
  return (
    typeof p === 'object' && p !== null && 'lineId' in p && typeof p.lineId === 'string' && group.includes(p.lineId)
  );
}

// Apply one arrivals response for group i (a list of line ids). Returns
// false if it was an older TfL snapshot than the one already applied.
function applyArrivals<P>(
  store: TrainStore,
  core: FeedCore<P>,
  net: Network,
  i: number,
  group: readonly LineId[],
  data: unknown,
  dataTime: number,
  now: number,
  received: number,
): boolean {
  if (!Array.isArray(data)) throw new Error('Invalid arrival data');
  const list: readonly unknown[] = data;
  // TfL's CDN edges hold different snapshots. An older one than we already
  // have would pull trains back and drop ones near a terminus until the next
  // response restores them.
  if (dataTime !== 0 && dataTime < (store.groupTimes.get(i) ?? 0)) return false;
  const computed = core.computeTrains(
    net,
    list.filter((p) => core.isPrediction(p, group)),
    now,
  );
  return applyComputed(store, core.mergeTrains, i, group, computed, dataTime, now, received);
}

// Put group i's newly computed trains (and its stations' arrivals) in the
// store in place of its last ones. Returns false, changing nothing, if
// dataTime is older than the snapshot already applied. The world map
// (apps/world) calls it with trains it computed itself.
function applyComputed(
  store: TrainStore,
  mergeTrains: typeof Core.mergeTrains,
  i: number,
  group: readonly LineId[],
  { trains, byStation, retired }: ComputedTrains,
  dataTime: number,
  now: number,
  received: number,
): boolean {
  if (dataTime !== 0 && dataTime < (store.groupTimes.get(i) ?? 0)) return false;
  if (dataTime !== 0) store.groupTimes.set(i, dataTime);
  mergeTrains(store.trains, trains, group, now, retired);
  const coveredLines = new Set(group);
  for (const [id, arrivals] of store.byStation) {
    const keep = arrivals.filter((a) => !coveredLines.has(a.lineId));
    if (keep.length > 0) store.byStation.set(id, keep);
    else store.byStation.delete(id);
  }
  for (const [id, arrivals] of byStation) {
    store.byStation.set(
      id,
      [...(store.byStation.get(id) ?? []), ...arrivals].toSorted((a, b) => a.eta - b.eta),
    );
  }
  store.groupUpdates.set(i, received);
  return true;
}

// Browser GTFS feeds stage the expensive matching and station-arrival work.
// The returned step is synchronous, so frames and callbacks see either the
// previous response or the complete new one, with both clocks updated.
export function* prepareComputedSteps(
  store: TrainStore,
  i: number,
  group: readonly LineId[],
  computed: ComputedTrains,
  dataTime: number,
  now: number,
): Generator<void, (received: number) => boolean, void> {
  if (dataTime !== 0 && dataTime < (store.groupTimes.get(i) ?? 0)) return () => false;
  const merge = yield* prepareMergeTrainsSteps(store.trains, computed.trains, group, now, computed.retired);
  const coveredLines = new Set(group);
  const arrivals = new Map<
    string,
    { readonly before: readonly Arrival[] | undefined; readonly after: readonly Arrival[] }
  >();
  for (const [id, before] of store.byStation) {
    const keep = before.filter((a) => !coveredLines.has(a.lineId));
    const incoming = computed.byStation.get(id);
    if (keep.length !== before.length || incoming) {
      const after = incoming ? [...keep, ...incoming].toSorted((a, b) => a.eta - b.eta) : keep;
      arrivals.set(id, { before, after });
    }
    yield;
  }
  for (const [id, incoming] of computed.byStation) {
    if (!arrivals.has(id)) arrivals.set(id, { before: undefined, after: incoming.toSorted((a, b) => a.eta - b.eta) });
    yield;
  }
  return (received) => {
    if (dataTime !== 0 && dataTime < (store.groupTimes.get(i) ?? 0)) return false;
    merge();
    for (const [id, prepared] of arrivals) {
      const current = store.byStation.get(id);
      // Another region can answer while this one prepares, even at a shared
      // station. Preserve its newer arrivals instead of restoring our copy.
      const after =
        current === prepared.before
          ? prepared.after
          : [
              ...(current ?? []).filter((a) => !coveredLines.has(a.lineId)),
              ...(computed.byStation.get(id) ?? []),
            ].toSorted((a, b) => a.eta - b.eta);
      if (after.length > 0) store.byStation.set(id, after);
      else store.byStation.delete(id);
    }
    if (dataTime !== 0) store.groupTimes.set(i, dataTime);
    store.groupUpdates.set(i, received);
    return true;
  };
}

export interface PollerOptions<P, Handle = ReturnType<typeof setTimeout>> {
  readonly store: TrainStore;
  readonly core: FeedCore<P>;
  readonly groups: readonly (readonly LineId[])[];
  readonly fetchGroup: (group: readonly LineId[], signal: AbortSignal) => Promise<SampledJSON>;
  readonly net: () => Network;
  readonly now: () => number;
  readonly clock?: () => number;
  readonly onUpdate?: () => void;
  // Indexes of the failed groups, and the error if the whole batch failed.
  readonly onSettled?: (result: { readonly failed: readonly number[]; readonly error: unknown }) => void;
  readonly isHidden?: () => boolean;
  readonly timers?: Timers<Handle>;
  readonly pollMs?: number;
  readonly retryMs?: number;
  readonly log?: { readonly warn: (...args: readonly unknown[]) => void };
}

export type Poller = Readonly<{ poll: () => Promise<void>; cancel: () => void }>;

// One pending timer at a time, whatever the handle type.
type OneTimer = Readonly<{ set: (fn: () => unknown, ms: number) => void; clear: () => void }>;
function oneTimer<H>(timers: Timers<H>): OneTimer {
  let handle: H | undefined;
  const set = (fn: () => unknown, ms: number): void => {
    handle = timers.setTimeout(fn, ms);
  };
  const clear = (): void => {
    timers.clearTimeout(handle);
  };
  return { set, clear };
}

// Fetch and apply group i unless `current()` says its batch was cancelled.
async function pollGroup<P, H>(
  options: PollerOptions<P, H>,
  batch: AbortSignal,
  current: () => boolean,
  group: readonly LineId[],
  i: number,
): Promise<void> {
  const { store, core, net, now, clock = () => Date.now(), onUpdate = () => {}, log = console } = options;
  try {
    const { data, dataTime } = await options.fetchGroup(group, batch);
    if (!current()) return;
    // Show healthy lines immediately; another group's hung connection
    // must not hold their fresh positions behind the others.
    if (applyArrivals(store, core, net(), i, group, data, dataTime, now(), clock())) onUpdate();
  } catch (error) {
    if (!current()) return;
    store.failedGroups.push(i);
    log.warn('Arrival group unavailable', group, error);
  }
}

// Polls every arrival group in parallel. Each group is applied as soon as it
// arrives (onUpdate), and onSettled({ failed, error }) runs once all have
// answered. cancel() aborts the batch in flight, and nothing it returns
// later is applied.
function createPoller<P, Handle>(options: PollerOptions<P, Handle>): Poller {
  const { store, groups, onSettled = () => {}, isHidden = () => false, pollMs = 20000, retryMs = 3000 } = options;
  const log = options.log ?? console;
  const timer = options.timers ? oneTimer(options.timers) : oneTimer(realTimers);
  let active: AbortController | null = null;
  // polls in a row with a failed group
  let failures = 0;

  function cancel(): void {
    timer.clear();
    if (active) active.abort();
    active = null;
  }

  async function poll(): Promise<void> {
    if (active) return;
    const batch = new AbortController();
    active = batch;
    timer.clear();
    const current = (): boolean => active === batch;
    try {
      store.failedGroups = [];
      await Promise.all(groups.map((group, i) => pollGroup(options, batch.signal, current, group, i)));
      if (!current()) return;
      onSettled({ failed: store.failedGroups, error: null });
    } catch (error) {
      if (!current()) return;
      log.warn('Arrival refresh failed', error);
      onSettled({ failed: store.failedGroups, error });
    } finally {
      if (current()) {
        active = null;
        // TfL's data is routinely 30 to 90 s old, and ETAs are absolute, so
        // old data isn't a reason to poll faster. Failures retry sooner, but
        // back off (3, 6, 12 s...) so a rate limit (429) isn't prolonged.
        failures = store.failedGroups.length > 0 ? failures + 1 : 0;
        const delay = failures > 0 ? Math.min(retryMs * 2 ** (failures - 1), pollMs) : pollMs;
        if (!isHidden()) timer.set(poll, delay);
      }
    }
  }

  return { poll, cancel };
}

export {
  createClient,
  validRoute,
  createRouteLoader,
  keepRoutes,
  createStore,
  applyArrivals,
  applyComputed,
  createPoller,
  isGroupPrediction,
};
